import { Show } from "solid-js";
import { useHomepageSingleBrand } from "./hooks/useHomepageSingleBrand";
import { useHomepageSingleBrandData } from "./hooks/useHomepageSingleBrandData";
import { AboutBrand } from "~/components/webstore/about_brand";
import { VoucherProperties } from "~/components/webstore/voucher_properties";
import { ActionsList } from "~/components/webstore_telescope/actions_list";
import { NearbyStoreMap } from "~/components/webstore_telescope/nearby_store_map";
import { HubbleBadge } from "~/components/webstore_telescope/hubble_badge";
import { clientRepo } from "~/server/apis/client_repo";
import { InitialSimulationData } from "~/hooks/denominations_grid/useDenominationGridState";
import { DottedLoader } from "~/widgets/loader";
import { WhatsappSharingInfo } from "../../../components/webstore_telescope/whatsapp_sharing_info";
import { useWebStoreContext } from "~/context/webstore_context";
import { BackgroundGradientV2 } from "~/components/webstore/background_gradient_v2";
import { DenominationsGridV2 } from "~/components/webstore_telescope/denominations_grid_v2/denominations_grid_v2";
import { getVoucherDescBrandingReplacement } from "~/data/partner_config";
import { goBack, openModalV2 } from "~/shared_states/modal";
import { CoinsTransactionsModalTelescope } from "~/components/webstore_telescope/coins_transactions_modal_telescope";
import { CoinsBalanceDisplay } from "~/components/webstore_telescope/coins_balance_display";

export type HomepageSingleBrandProps = {
  brandKey: string;
};

export function HomepageSingleBrand(props: HomepageSingleBrandProps) {
  const { getClientId, getMode } = useWebStoreContext();

  const dataHook = useHomepageSingleBrandData({
    brandKey: props.brandKey,
    clientId: getClientId(),
    mode: getMode() as "hubble" | "rnr" | "sdk",
  });
  return (
    <Show
      when={!dataHook.isLoading() && !dataHook.error() && dataHook.brand()}
      fallback={
        <Show
          when={dataHook.error()}
          fallback={
            <div class="flex min-h-screen items-center justify-center">
              <DottedLoader />
            </div>
          }
        >
          <div class="flex min-h-screen items-center justify-center">
            <p class="text-para-3-regular text-text-normal-secondary">
              {dataHook.error()}
            </p>
          </div>
        </Show>
      }
    >
      <_HomepageSingleBrand brandKey={props.brandKey} dataHook={dataHook} />
    </Show>
  );
}

/**
 * Single brand homepage view - Shows a brand page-like experience when only 1 brand is available
 * Design: https://www.figma.com/design/uTQhl6K0EfVzl9uxl4Y4o1/Demand-master?node-id=268-15813
 */
export function _HomepageSingleBrand(props: {
  brandKey: string;
  dataHook: ReturnType<typeof useHomepageSingleBrandData>;
}) {
  const { dataHook } = props;
  const { getPartnerConfigV3, openContactSupport, getClientId } =
    useWebStoreContext();
  // Fetch data from API

  // Business logic with fetched data
  const hookData = useHomepageSingleBrand({
    brandKey: props.brandKey,
    brand: dataHook.brand(),
    brandExtraMetadata: dataHook.brandExtraMetadata(),
    simulateTransactionDetails: dataHook.simulateTransactionDetails(),
    simulateError: dataHook.simulateError(),
  });

  function buildDenominationGrid() {
    const brand = hookData.brand;
    const simulateTransactionDetails = hookData.simulateTransactionDetails;

    if (!brand) return null;

    return (
      <DenominationsGridV2
        initialVariantId={
          brand.variantDetailedResponse?.find((variant) => variant.isDefault)
            ?.variantId ?? null
        }
        initialConsumeCoins={true}
        brand={brand}
        isCoinsOnly={simulateTransactionDetails?.paymentSource === "COINS_ONLY"}
        allowRewardCoins={false}
        showHubbleCoinRewards={false}
        coinsBalance={hookData.coinsBalance}
        isRewardTypePostPaid={brand.rewardType === "POSTPAID"}
        simulateFunction={(params) => clientRepo.simulate(params)}
        initiateOrder={(props) => hookData.onClickGetThisCard?.(props)}
        ctaState={hookData.ctaState!}
        setCtaState={hookData.setCtaState!}
      />
    );
  }

  const { refreshV4HomePage } = useWebStoreContext();

  return (
    <div class="relative flex min-h-screen w-full flex-col items-center bg-background-normal-primary">
      {/* Background Gradient */}
      <Show when={getPartnerConfigV3().showGradient}>
        <BackgroundGradientV2
          color={getPartnerConfigV3().gradientColor ?? "#001EFF"}
        />
      </Show>

      {/* Hero Section with Greeting */}
      <div class="z-[1] flex w-full flex-col items-center gap-0 px-3 pb-4 pt-8">
        {/* Greeting Text */}
        <div class="flex w-full max-w-[1024px] flex-col items-center gap-2 text-center">
          <p class="text-para-3-regular text-text-normal-secondary">
            {getPartnerConfigV3().homepage?.smallTitle ??
              `Hi ${hookData.userName} 👋`}
          </p>
          <h1 class="text-title-2-semi-bold text-text-normal-primary sm:text-display-4-medium">
            {getPartnerConfigV3().homepage?.largeTitle ?? (
              <>
                Shop using coins on
                <br />
                {hookData.brand?.voucherProductMetadata.title}
              </>
            )}
          </h1>
        </div>

        <Show
          fallback={<div class="mt-4 sm:mt-4"></div>}
          when={getPartnerConfigV3().showCoins}
        >
          <div class="mt-4"></div>
          <CoinsBalanceDisplay
            balance={hookData.coinsBalance ?? 0}
            onClick={() => {
              // Navigate to profile or coins page
              // navigate("/profile");
              openModalV2(
                () => (
                  <CoinsTransactionsModalTelescope
                    balance={hookData.coinsBalance ?? 0}
                    userName={hookData.userName ?? ""}
                    refreshV4HomePage={refreshV4HomePage}
                    enableRedeemCode={getPartnerConfigV3().allowCodeApplication}
                    onClose={() => {
                      goBack();
                    }}
                  />
                ),
                {
                  expandToFullHeight: true,
                }
              );
            }}
          />
        </Show>
      </div>

      {/* Main Content */}
      <div class="w-full px-3 py-4 sm:max-w-[360px] sm:px-0">
        <div class="flex flex-col gap-6">
          {/* GC Amount Box (Denomination Grid) */}
          <div class="overflow-hidden rounded-[16px] bg-background-mapping-reverse p-1">
            {buildDenominationGrid()}
          </div>

          {/* Additional Content */}
          <div class="flex flex-col gap-6 px-1">
            {/* WhatsApp Sharing Info */}
            <Show when={getPartnerConfigV3().showWhatsappDeliveryMessage}>
              <WhatsappSharingInfo />
            </Show>

            {/* Voucher Properties */}
            <VoucherProperties
              voucherHighlights={hookData.brandExtraMetadata!.voucherHighlights}
            />

            {/* Actions List */}
            <Show when={hookData.brand}>
              <ActionsList
                brandId={hookData.brand!.externalId}
                productId={hookData.brand!.externalId}
                onContactSupport={openContactSupport}
                showContactSupport={getPartnerConfigV3().showContactSupport}
              />
            </Show>

            {/* Nearby Store Map */}
            <Show when={hookData.brand?.voucherProductMetadata.storeLocatorUrl}>
              <NearbyStoreMap
                storeLocatorUrl={
                  hookData.brand!.voucherProductMetadata.storeLocatorUrl!
                }
              />
            </Show>

            {/* About the Brand */}
            <Show
              when={
                hookData.brandExtraMetadata?.brandDescription && hookData.brand
              }
            >
              <AboutBrand
                brandName={hookData.brand!.voucherProductMetadata.title}
                brandDescription={hookData.brandExtraMetadata!.brandDescription!.replaceAll(
                  "Hubble",
                  getVoucherDescBrandingReplacement(getClientId())
                )}
                brandLogoUrl={
                  hookData.brand!.voucherProductMetadata.logoUrl ?? ""
                }
              />
            </Show>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Show when={getPartnerConfigV3().showHubbleBranding}>
        <div class="mt-8"></div>
        <div class="fixed bottom-0  flex w-full flex-col gap-2 bg-background-normal-primary px-3 pb-2">
          <div class="h-px w-full bg-stroke-1" />
          <div class="flex w-full flex-col items-center">
            <HubbleBadge />
          </div>
        </div>
      </Show>
    </div>
  );
}
